import {
  ApplicationConfig,
  isDevMode,
  provideBrowserGlobalErrorListeners,
  provideAppInitializer,
  inject,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient } from '@angular/common/http';
import { provideMarkdown } from 'ngx-markdown';
import { provideTransloco, TranslocoService, getBrowserLang } from '@jsverse/transloco';
import { WHITE_LABEL_CONFIG } from '@crewdle/white-label/angular';

import { routes } from './app.routes';
import { getFirebaseApp } from './core/firebase';
import { FirebaseService } from './core/services/firebase.service';
import { ThemeService } from './core/theme.service';
import { TranslocoHttpLoader } from '../transloco-loader';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes),
    provideHttpClient(),
    provideMarkdown(),
    provideTransloco({
      config: {
        availableLangs: ['en', 'fr'],
        defaultLang: 'fr',
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader,
    }),
    provideAppInitializer(() => { getFirebaseApp(); }),
    provideAppInitializer(() => { inject(ThemeService); }),
    { provide: WHITE_LABEL_CONFIG, useFactory: () => ({ firestore: inject(FirebaseService).globalFirestore }) },
    // OrgContext.init() is intentionally NOT a bootstrap initializer: it runs a
    // chain of Firestore reads that would otherwise block first paint for every
    // route — including public ones (/login, /signup, invitations) that don't
    // need org context at all. The `redirectUnauthorizedTo` auth guard runs
    // init() (guarded against rejection) once a session is established, before
    // any authenticated route activates, so org context is still ready in time.
    provideAppInitializer(() => {
      const translate = inject(TranslocoService);
      const savedLang = localStorage.getItem('lang');

      if (savedLang && ['en', 'fr'].includes(savedLang)) {
        translate.setActiveLang(savedLang);
        return;
      }

      // Check root domain cookie
      try {
        const cookies = document.cookie.split(';');
        for (const c of cookies) {
          const t = c.trim();
          if (t.startsWith('crewdle_lang=')) {
            const v = decodeURIComponent(t.substring('crewdle_lang='.length));
            if (v === 'en' || v === 'fr') {
              translate.setActiveLang(v);
              localStorage.setItem('lang', v);
              return;
            }
          }
        }
      } catch {}

      const browserLang = getBrowserLang();
      if (browserLang?.match(/en|fr/)) {
        translate.setActiveLang(browserLang);
      }
    }),
  ],
};
